/**
 * Recently Viewed Products Tracking and Rendering
 */
const RECENTLY_VIEWED_KEY = "recently_viewed_products";
const MAX_STORED_PRODUCTS = 12;
const DEFAULT_RENDER_LIMIT = 8;

const getStoredProducts = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENTLY_VIEWED_KEY)) || [];
  } catch (e) {
    return [];
  }
};

const saveProducts = (products) => {
  try {
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(products));
  } catch (e) {
    // Ignore write errors (e.g. private mode / storage blocked)
  }
};

const parseCurrentProduct = (currentProductElement) => {
  const productData = currentProductElement?.dataset?.product;
  if (!productData) return null;

  try {
    return JSON.parse(productData);
  } catch (e) {
    return null;
  }
};

const updateStoredProducts = (products, currentProduct) => {
  if (!currentProduct?.handle) return { products, currentHandle: null };

  const deduped = products.filter(
    (product) =>
      product.id !== currentProduct.id &&
      product.handle !== currentProduct.handle,
  );

  const updated = [
    {
      ...currentProduct,
      timestamp: Date.now(),
    },
    ...deduped,
  ].slice(0, MAX_STORED_PRODUCTS);

  saveProducts(updated);

  return { products: updated, currentHandle: currentProduct.handle };
};

const setCarouselVisible = (carousel) => {
  if (carousel?.classList?.contains("hidden")) {
    carousel.classList.remove("hidden");
  }
};

const renderRecentlyViewed = ({
  products,
  currentHandle,
  carousel,
  container,
  renderLimit,
}) => {
  if (!carousel || !container || renderLimit <= 0 || products.length === 0) {
    return;
  }

  const handles = [];
  for (let i = 0; i < products.length && handles.length < renderLimit; i += 1) {
    const handle = products[i]?.handle;
    if (handle && handle !== currentHandle) {
      handles.push(handle);
    }
  }

  if (handles.length === 0) return;

  const root = window.Shopify?.routes?.root || "/";
  const searchPath = root.endsWith("/") ? "search" : "/search";
  const searchUrl = `${root}${searchPath}?section_id=recently-viewed-ajax&type=product&q=${encodeURIComponent(handles.join(","))}`;

  fetch(searchUrl)
    .then((response) => {
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      return response.text();
    })
    .then((text) => {
      if (!text || text.trim().length === 0) return;

      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");
      const slides = doc.querySelectorAll(".swiper-slide");

      if (slides.length > 0) {
        const fragment = document.createDocumentFragment();
        slides.forEach((slide) => {
          fragment.appendChild(slide);
        });
        container.replaceChildren(fragment);
        setCarouselVisible(carousel);
      } else {
        container.innerHTML = text;
        setCarouselVisible(carousel);
      }

      customElements.whenDefined("swiper-carousel").then(() => {
        requestAnimationFrame(() => {
          if (carousel.swiper) {
            carousel.swiper.update();
            return;
          }

          window.dispatchEvent(new Event("resize"));

          if (typeof carousel.initImmediately === "function") {
            carousel.initImmediately();
          } else if (typeof carousel.init === "function") {
            carousel.init();
          }
        });
      });
    })
    .catch(() => {});
};

function initRecentlyViewed() {
  const currentProductElement = document.querySelector(".product-main--section");
  const recentlyViewedCarousel = document.querySelector(
    'swiper-carousel[data-id="recently-viewed"]'
  );
  const recentlyViewedProductsContainer = recentlyViewedCarousel?.querySelector(".swiper-wrapper");

  const initialProducts = getStoredProducts();
  const currentProduct = parseCurrentProduct(currentProductElement);
  const { products: updatedProducts, currentHandle } = updateStoredProducts(
    initialProducts,
    currentProduct,
  );

  const renderLimit = Math.max(
    1,
    Number.parseInt(
      recentlyViewedCarousel?.dataset?.productsLimit || "",
      10,
    ) || DEFAULT_RENDER_LIMIT,
  );

  renderRecentlyViewed({
    products: updatedProducts,
    currentHandle,
    carousel: recentlyViewedCarousel,
    container: recentlyViewedProductsContainer,
    renderLimit,
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initRecentlyViewed);
} else {
  initRecentlyViewed();
}